<template lang="pug">
	div.flex.justify-center
		Loader(v-if="isSendingInvites" info="Sending invites...")

		// name of company
		transition(name="slideThru")
			form.max-w-4xl.w-full.px-4(@submit.prevent="next('intro', {do: 'createNewCompany', payload: companyName})" v-if="stage === 'company-name'")
				div.text-3xl.font-semibold 
					p.text-gray-9 Welcome!
					p.text-gray-6.-mt-2 First things first...
				input.ghost.large.mt-6(v-focus v-model="companyName" placeholder="What’s your company’s name?")
				//p.help-text You can change this later
				
				transition(name="fade")
					.flex.mt-4.items-center(v-if="companyName.length > 2")
						button.w-auto.main.big.mr-2.p-4(type="submit") Save name
						p.text-gray-5.text-sm press #[b ENTER ↵]
				
				.h-12
		
		// intro
		transition(name="slideThru")
			form.max-w-md.px-4(@submit.prevent="next('purpose')" v-if="stage === 'intro'")
				.flex.justify-center.-mt-8.items-start
					img.mb-4(width="300" alt="Ice cream illustration" src="../assets/images/iceCream.png")
				
				h2.text-3xl.font-semibold.mb-6
					span.text-gray-6 {{ companyName }}’#[span.pr-1(v-if="companyName.slice(-1) != 's'") s] workspace is live! 
					br
					| Set your teams up for success

				p.mb-8 We’ll walk you through a powerful ORKs framework for creating goals that bring alignment and focus to your team. In a few minutes, you’ll:
				.ml-4.text-gray-7
					.flex.mb-3
						eva-icon.icon.text-green-6(name="checkmark-circle-2") 
						span.ml-2 Have a public, shared vision for your company
					.flex.mb-3
						eva-icon.icon.text-green-6(name="checkmark-circle-2") 
						span.ml-2 Some KPI-driven goals to guide your teams
					.flex.mb-8
						eva-icon.icon.text-green-6(name="checkmark-circle-2") 
						span.ml-2 And a space for teams to contribute to goal-setting, and adopt the OKRs framework
				.flex.mt-4.items-center
					button.w-auto.main.big.mr-2.p-4(v-focus type="submit") Awesome, let’s go!
					p.text-gray-5.text-sm press #[b ENTER ↵]
				.h-12
		// purpose
		transition(name="slideThru")
			form.w-full.max-w-md.px-4(@submit.prevent="next('objectives', {do: 'updateCompany', payload: {id: profile.companyId, change: {purpose: purpose}}})" v-if="stage === 'purpose'")
				h2.text-3xl.font-semibold.mb-2 #[span.text-gray-6 First up...] #[br] Define your purpose
				p.mb-8 The mission statement of your company, why you exist.
				input(v-model="purpose" placeholder="What is your company’s purpose?")
				p.help-text.mb-4 You can always edit this later
				.rounded-lg.bg-indigo-1.p-4
					h4.text-indigo-4.mb-2 Example purpose
					h3.text-indigo-9 “To help teams bring their projects to life
					.h-px.bg-indigo-2.w-full.my-4
					.text-indigo-9
						.flex.mb-2.items-center
							eva-icon.icon.text-indigo-6(name="checkmark-outline") 
							span.ml-2 Short and sweet
						.flex.mb-2.items-center
							eva-icon.icon.text-indigo-6(name="checkmark-outline") 
							span.ml-2 Anchors business to a clear market
						.flex.mb-2.items-center
							eva-icon.icon.text-indigo-6(name="checkmark-outline") 
							span.ml-2 Loose enough to explore many oppurtunities
						
				transition(name="fade")
					.flex.mt-4.items-center(v-if="purpose.length > 4")
						button.w-auto.main.big.mr-2.p-4(type="submit") Set purpose
						p.text-gray-5.text-sm press #[b ENTER ↵]
				.h-12
		// objectives
		transition(name="slideThru")
			form.w-full.max-w-md.px-4(@submit.prevent="next('invites', {do: 'createObjectivesFromArray', payload: {objectives: objectives}})" v-if="stage === 'objectives'")
				h2.text-3xl.font-semibold.mb-2 #[span.text-gray-6 Amazing!] #[br] What are your objectives?
				p.mb-8 What are your top priorities for {{companyName}} in the next 12 months? Set realistic targets. Add between #[b.text-gray-9 2 - 4 objectives], too many will get teams distracted by competing priorities.
				
				form.relative(@submit.prevent="addObjective")
					input.h-12#objectiveInput(v-model="objective" placeholder="Add an objective" :disabled="objectives.length >= 4")
					button.icon.main.absolute.right-0.top-0.mt-1.mr-1.h-10.w-10(:disabled="!objective.length || objectives.length >= 4" type="submit")
						eva-icon(name="plus-outline")
					p.help-text.mb-4 You can always edit this later
				
				.pb-4
					transition(name="collapse")
						h4.text-gray-5.h-6(v-if="objectives.length") Objectives
					transition-group(name="collapse")
						.flex.h-8.items-center(v-for="(obj, index) in objectives" :key="index")
							eva-icon.icon.text-red-4.cursor-pointer(@click="deleteObjective(index)" name="minus-circle-outline")
							span.ml-2 {{obj}}
				
				transition(name="fade")
					.flex.mt-2.mb-4.items-center(v-if="objectives.length >= 2 && objectives.length <= 4")
						button.w-auto.main.big.mr-2#objBtn.p-4(type="submit") Set objectives
						p.text-gray-5.text-sm(v-if="objectives.length >= 4") press #[b ENTER ↵]
				
				.rounded-lg.bg-indigo-1.p-4
					h4.text-indigo-4.mb-2 Example objectives
					p.text-indigo-9.opacity-75 To help teams bring their projects to life
					.flex
						.ml-1
							.connector.h-6
							.connector.h-10
							.connector.h-10
						.text-indigo-9.pt-2.pl-2.font-semibold
							p.text-indigo-9.mb-2 Finish raising new capital for our growth needs
							p.text-indigo-9.mb-2 Build an integration ecosystem
							p.text-indigo-9 Grow and improve customer satisfaction
				.h-12
		// invties
		transition(name="slideThru")
			form.w-full.max-w-md.px-4(@submit.prevent="finish()" v-if="stage === 'invites'")
				.flex.justify-center.-mt-4.items-start
					img.mb-4(width="300" alt="papers flying illustration" src="../assets/images/papers.png")
					
				h2.text-3xl.font-semibold.mb-2 #[span.text-gray-6 You’ve built the foundation!] #[br] Let’s get your team onboard!
				p.mb-8 Invite your team to Wangll so they can set goals based on your objectives. We’ll walk them through best practices and adopting the OKRs mindset.
				
				form.relative.mb-4(@submit.prevent="validate")
					input#emailInput(v-model="email" placeholder="Your teammates’ email address..." :class="{error: emailError}")
					button.icon.main.absolute.right-0.top-0.mt-1.mr-1.h-10.w-10(:disabled="!email.length" type="submit")
						eva-icon(name="plus-outline")
					transition(name="collapse")
						p.error-text.h-6(v-if="emailError") {{emailError}}
						p.help-text You can organise them into teams later
				
				.flex.items-center
					button.flex.py-1.px-2.-ml-2.text-indigo-9.font-semibold.w-auto.shadow-none(@click.prevent="skip") Skip
					eva-icon.icon.text-gray-6.spin(v-if="skipping" name="loader-outline")
				
				.pb-4
					transition(name="collapse")
						h4.text-gray-5.h-6(v-if="teammates.length") Teammates
					transition-group(name="collapse")
						.flex.h-20.items-center.justify-between.border-t(v-for="(teammate, index) in teammates" :key="index")
							.flex.items-center
								Avatar(:profile="teammate" size="medium")
								span.ml-2 {{teammate.email}}
							eva-icon.icon.text-gray-4.cursor-pointer(@click="deleteTeammate(index)" name="minus-circle-outline")

				transition(name="fade")
					.flex.mt-2.mb-4.items-center(v-if="teammates.length")
						button.w-auto.main.big.mr-2(type="submit") Send invites	
				.h-12
				
					
			
		// finish
		transition(name="slideThru")
			.w-full.max-w-md.px-4(v-if="stage === 'finish'")
				.flex.justify-center.-mt-4.items-start
					img.mb-4(width="300" alt="dog jump illustration" src="../assets/images/dogJump.png")
				h2.text-3xl.font-semibold.mb-2 You’re all set!
				p.mb-8 You’ve created an environment to allow your team to reach {{companyName}}’#[span.pr-1(v-if="companyName.slice(-1) != 's'") s] goals! While they’re getting on board, feel free to explore or customize Wangll to your needs.
				
				.flex.items-center
					button.main.big.mr-2.p-4.w-full(:diable="touring" @click="goToTour") Check out your dashboard
					eva-icon.icon.text-gray-6.spin(v-if="touring" name="loader-outline")

				.flex.items-center.mt-6
					.h-px.flex-1.bg-gray-3.mt-1
					span.px-2.text-gray-5 or
					.h-px.flex-1.bg-gray-3.mt-1
				router-link.button.mt-6(to="/settings") Tweak settings and organise teams
				.h-12
</template>

<script>
import { ref } from "@vue/composition-api";
import { auth } from "../firebase.config";
import { mapGetters, mapActions } from "vuex";
import Avatar from "./Avatar";
import Loader from "./Loader";

export default {
  components: { Avatar, Loader },
  computed: { ...mapGetters(["profile"]) },
  setup(props, { root }) {
    const companyName = ref("");
    const purpose = ref("");
    const objective = ref("");
    const objectives = ref([]);
    const email = ref("");
    const teammates = ref([]);
    const emailError = ref("");
    const stage = ref("company-name");
    const skipping = ref(false);
    const touring = ref(false);
    const isSendingInvites = ref(false);

    const getters = root.$store.getters;

    if (getters.company) {
      companyName.value = getters.company.name;
      stage.value = "intro";
      if (getters.company.purpose) {
        purpose.value = getters.company.purpose;
        stage.value = "objectives";
      }
      if (getters.objectives.length) stage.value = "invites";
    }

    function next(stageName, action) {
      if (action) root.$store.dispatch("runAction", action);
      stage.value = stageName;
      window.scrollTo(0, 0);
    }

    function addObjective() {
      objectives.value.push(objective.value);
      objective.value = "";
      document.getElementById("objectiveInput").focus();
      if (objectives.value.length >= 4) {
        document.getElementById("objBtn").focus();
      }
    }
    function deleteObjective(index) {
      objectives.value.splice(index, 1);
    }

    async function addTeammate(newEmail) {
      const colour = await root.$store.dispatch("pickColour");
      const teammate = {
        colour: colour,
        email: newEmail,
        displayName: "",
        teams: []
      };
      teammates.value.push(teammate);
      email.value = "";
      document.getElementById("emailInput").focus();
    }
    function deleteTeammate(index) {
      teammates.value.splice(index, 1);
    }

    async function validate() {
      const lintedEmail = await root.$store.dispatch("lintEmail", email.value);
      const isEmail = await root.$store.dispatch("isEmail", lintedEmail);

      if (!isEmail) emailError.value = "That’s not a proper email address";
      else {
        email.value = lintedEmail;
        emailError.value = "";
      }

      if (emailError.value) return;
      else addTeammate(lintedEmail);
    }

    function finish() {
      root.$store.dispatch("getObjectives", this.profile.companyId);
      isSendingInvites.value = true;
      //dd
      root.$store.dispatch("inviteTeammates", teammates.value).then(() => {
        stage.value = "finish";
        isSendingInvites.value = false;

        const invites = [];
        teammates.value.forEach(teammate => {
          return invites.push(teammate.email);
        });
        root.$store.dispatch("updateCompany", {
          id: root.$store.getters.profile.companyId,
          change: { invites: invites }
        });

        root.$store.dispatch("updateUser", {
          uid: auth.currentUser.uid,
          dontFetch: true,
          change: { onboarded: true }
        });
      });
    }

    function skip() {
      root.$store.dispatch("getObjectives", this.profile.companyId);
      skipping.value = true;
      root.$store
        .dispatch("updateUser", {
          uid: auth.currentUser.uid,
          dontFetch: true,
          change: { onboarded: true }
        })
        .then(() => {
          skipping.value = false;
          stage.value = "finish";
        });
    }

    function goToTour() {
      touring.value = true;
      console.log(auth.currentUser.uid);

      root.$store.dispatch("getUserProfile", auth.currentUser).then(() => {
        touring.value = false;
        Intercom("startTour", 115543);
      });
    }

    return {
      companyName,
      purpose,
      stage,
      next,
      objective,
      objectives,
      addObjective,
      deleteObjective,
      email,
      teammates,
      emailError,
      validate,
      addTeammate,
      deleteTeammate,
      isSendingInvites,
      finish,
      skip,
      skipping,
      touring,
      goToTour
    };
  },
  directives: {
    focus: {
      inserted(el) {
        el.focus();
      }
    }
  }
};
</script>

<style lang="sass" scoped>
.connector
	@apply w-3 -mb-2 rounded-bl-lg border-l-2 border-b-2 border-indigo-3
form.max-w-md
	@apply absolute
</style>
